<template>
  <!-- Manager de vinculación BOQ ↔ WBS (master-detail en 2 columnas).
       IZQUIERDA: lista expandible de BOQs (grupo) → sus items/detalles (hijos).
       DERECHA: detalle del item elegido (luego el árbol WBS para vincular). -->
  <div class="grid gap-4" style="grid-template-columns: minmax(300px, 380px) 1fr; grid-template-rows: 70vh;">
    <!-- IZQUIERDA -->
    <epc-panel :title="$t('boqs')" tone="primary" flush>
      <template #actions>
        <button title="Desplegar todo" aria-label="Desplegar todo" class="p-1.5 rounded shrink-0 text-white/80 hover:text-white hover:bg-white/15"><svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-unfold-vertical" aria-hidden="true"><path d="M12 22v-6"></path><path d="M12 8V2"></path><path d="M4 12H2"></path><path d="M10 12H8"></path><path d="M16 12h-2"></path><path d="M22 12h-2"></path><path d="m15 19-3 3-3-3"></path><path d="m15 5-3-3-3 3"></path></svg></button>
      </template>

      <div v-if="!nodes.length" class="p-4 text-xs text-gray-400 text-center">
        {{ $t('no_results') }}
      </div>

      <expandable-list v-else :items="nodes" :selected-id="selectedNodeId" @select="onSelect" />
    </epc-panel>

    <!-- DERECHA: card resumen (arriba, fija) + card árbol WBS (abajo, su body scrollea). -->
    <div class="min-w-0 h-full flex flex-col">
      <template v-if="selectedItem">
        <!-- Card resumen del item seleccionado -->
        <div class="bg-white rounded-lg border border-gray-200 mb-4 flex-shrink-0" style="padding: 0.875rem">
          <div class="min-w-0">
            <div class="flex items-center justify-between gap-2">
              <div class="mono text-gray-400 min-w-0" style="font-size: 9.5px; word-break: break-all">{{ selectedItem.line_pedido || selectedItem.code }}</div>
              <span
                v-if="coverageStatusKey"
                class="inline-flex items-center rounded flex-shrink-0"
                :style="{ ...coverageStatusStyle, padding: '1px 8px', fontSize: '10px', fontWeight: 700 }"
              >
                {{ $t(coverageStatusKey) }}
              </span>
            </div>
            <div class="font-semibold truncate" style="font-size: 12px; color: rgb(30,48,70)">{{ selectedItem.description || '—' }}</div>
            <div class="flex items-center justify-between gap-2" style="margin-top: 0.375rem">
              <div v-if="!noMeasurementLink" class="flex items-center gap-2">
                <span
                  class="inline-flex items-center gap-1 rounded uppercase"
                  style="padding: 2px 6px; font-size: 9px; font-weight: 700; letter-spacing: .03em; color: rgb(58,107,200); background: rgba(58,107,200,.08); border: 1px solid rgba(58,107,200,.267)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-list-checks"><path d="M13 5h8"></path><path d="M13 12h8"></path><path d="M13 19h8"></path><path d="m3 17 2 2 4-4"></path><path d="m3 7 2 2 4-4"></path></svg>
                  {{ $t('mode') }}: {{ viewMode === 'linked' ? $t('mode_linked') : $t('mode_linking') }}
                </span>
                <button type="button" class="underline text-gray-500 hover:text-gray-700" style="font-size: 10px" @click="toggleViewMode">{{ $t('change_mode') }}</button>
              </div>
              <span v-else></span>

              <!-- Marca la línea BOQ como "sin mediciones" (solo si no tiene mediciones vinculadas). -->
              <button
                v-if="canMarkNoLink"
                type="button"
                class="inline-flex items-center gap-1 rounded flex-shrink-0"
                :style="noMeasurementLink
                  ? { padding: '2px 8px', fontSize: '10px', fontWeight: 700, color: '#fff', background: 'rgb(87,83,78)', border: '1px solid rgb(87,83,78)' }
                  : { padding: '2px 8px', fontSize: '10px', fontWeight: 600, color: 'rgb(120,113,108)', background: '#f5f5f4', border: '1px solid rgba(120,113,108,.35)' }"
                :title="$t('no_measurement_link')"
                @click="toggleNoMeasurementLink"
              >
                <b-icon pack="fas" icon="link-slash" size="is-small"></b-icon>
                {{ $t('no_measurement_link') }}
              </button>
            </div>
          </div>

          <div class="grid grid-cols-3" style="gap: 0.625rem; margin-top: 0.75rem">
            <div class="rounded border border-gray-200" style="padding: 0.5rem 0.75rem">
              <div class="uppercase text-gray-500" style="font-size: 9px; font-weight: 600; letter-spacing: .03em">Cantidad total</div>
              <div class="font-bold tabular-nums" style="font-size: 15px; color: rgb(30,48,70)">{{ qtyText }} <span class="text-gray-500" style="font-size: 10px; font-weight: 500">{{ selectedItem.unit }}</span></div>
              <div class="text-gray-400 tabular-nums" style="font-size: 9.5px">≈ {{ formatMoney(selectedItem.price_total) }}</div>
            </div>
            <div class="rounded" style="padding: 0.5rem 0.75rem; background: rgba(39,174,96,.063); border: 1px solid rgba(39,174,96,.333)">
              <div class="uppercase" style="font-size: 9px; font-weight: 600; letter-spacing: .03em; color: rgb(28,130,70)">{{ $t('assigned') }}</div>
              <div class="font-bold tabular-nums" style="font-size: 15px; color: rgb(28,130,70)">{{ assignedText }} <span style="font-size: 10px; font-weight: 500">{{ selectedItem.unit }}</span></div>
              <div class="tabular-nums" style="font-size: 9.5px; color: rgba(28,130,70,.667)">{{ selectedCount }} {{ $t('selected_plural') }}</div>
            </div>
            <div class="rounded" style="padding: 0.5rem 0.75rem; background: rgba(58,107,200,.05); border: 1px solid rgba(58,107,200,.333)">
              <div class="uppercase" style="font-size: 9px; font-weight: 600; letter-spacing: .03em; color: rgb(58,107,200)">{{ $t('pending') }}</div>
              <div class="font-bold tabular-nums" style="font-size: 15px; color: rgb(58,107,200)">{{ pendingText }} <span style="font-size: 10px; font-weight: 500">{{ selectedItem.unit }}</span></div>
            </div>
          </div>

          <div class="flex items-center justify-between gap-2" style="margin-top: 0.75rem">
            <button
              type="button"
              class="rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
              style="padding: 0.375rem 0.75rem; font-size: 11px; font-weight: 500"
              @click="clearSelection"
            >
              {{ $t('cancel') }}
            </button>
            <button
              type="button"
              class="flex items-center gap-2 rounded text-white"
              style="padding: 0.375rem 0.75rem; font-size: 11px; font-weight: 600; background: rgb(58,107,200)"
              :disabled="isSavingLinks"
              @click="saveLinks"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-save"><path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"></path><path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7"></path><path d="M7 3v4a1 1 0 0 0 1 1h7"></path></svg>
              {{ isSavingLinks ? $t('saving') : $t('save') }}
            </button>
          </div>
        </div>

        <!-- Card árbol WBS: header fijo + body scrollable. -->
        <div v-if="!noMeasurementLink" class="bg-white rounded-lg border border-gray-200 overflow-hidden flex-1 min-h-0 flex flex-col">
          <div class="text-white" style="background: rgb(61,92,158); padding: 0.625rem 0.75rem">
            <div class="flex items-center justify-between gap-3" style="font-size: 11px; font-weight: 600">
              <span>{{ viewMode === 'linked' ? $t('mode_linked') : 'Árbol WBS — marca dónde vincular' }}</span>
              <span style="font-size: 9px; opacity: .8">Relación completa del Elemento de Control</span>
            </div>
            <div v-if="viewMode === 'linking'" class="flex items-center gap-2" style="margin-top: 0.5rem; padding: 6px 8px; border-radius: 4px; background: rgba(255,255,255,.12); font-size: 9px; font-weight: 600">
              <span>Planificación interna</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"></path></svg>
              <span>Programa del cliente</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"></path></svg>
              <span>Bill of Quantities (BOQ)</span>
            </div>
          </div>
          <div class="flex-1 min-h-0 overflow-y-auto">
            <!-- Modo Vinculación: árbol WBS -->
            <template v-if="viewMode === 'linking'">
              <div v-if="wbsLoading" class="flex items-center justify-center gap-2 text-gray-400" style="padding: 2rem; font-size: 11px">
                <b-icon icon="spinner" pack="fas" size="is-small" class="fa-spin"></b-icon>
                {{ $t('loading') }}
              </div>
              <div v-else-if="wbsError" class="text-center text-gray-400" style="padding: 2rem; font-size: 11px">
                {{ $t('error_loading_activities') }}
              </div>
              <div v-else-if="!wbsNodes.length" class="text-center text-gray-400" style="padding: 2rem; font-size: 11px">
                {{ $t('no_results') }}
              </div>
              <wbs-tree
                v-else
                :nodes="wbsNodes"
                :unit="selectedItem.unit || ''"
                :current-boq-id="selectedItem.id"
                :loader="loadWbsChildren"
                :checked="currentChecked"
                :quantities="currentQuantities"
                :coverage-full="coverageFull"
                @check="onCheck"
                @qty="onQty"
              />
            </template>

            <!-- Modo Mediciones vinculadas: lista de seleccionadas, con checkbox para desvincular. -->
            <template v-else>
              <div v-if="!linkedMedsMarked.length" class="text-center text-gray-400" style="padding: 2rem; font-size: 11px">
                {{ $t('no_results') }}
              </div>
              <table v-else class="lm-table">
                <thead>
                  <tr>
                    <th style="width: 34px"></th>
                    <th>{{ $t('name') }}</th>
                    <th class="is-right">{{ $t('quantity') }}</th>
                    <th>{{ $t('unit') }}</th>
                    <th class="is-center">{{ $t('status') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="m in linkedMedsMarked" :key="'lm-' + m.id" :class="{ 'lm-row--excess': m.linked && m._excess, 'lm-row--off': !m.linked }">
                    <td class="is-center">
                      <input type="checkbox" :checked="m.linked" :title="$t('unlink')" @change="toggleLinked(m)" />
                    </td>
                    <td>
                      <span class="inline-flex items-center gap-1">
                        <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lm-ec-icon"><path d="M13 5h8"></path><path d="M13 12h8"></path><path d="M13 19h8"></path><path d="m3 17 2 2 4-4"></path><path d="m3 7 2 2 4-4"></path></svg>
                        {{ m.name || m.code || ('#' + m.id) }}
                      </span>
                    </td>
                    <td class="is-right">{{ formatQty(m.quantity) }}</td>
                    <td>{{ m.unit || '—' }}</td>
                    <td class="is-center">
                      <span v-if="!m.linked" class="boq-status boq-status--off">{{ $t('will_be_removed') }}</span>
                      <b-tooltip
                        v-else-if="m._excess"
                        :label="$t('boq_status_excess_other')"
                        position="is-left"
                        type="is-danger"
                        multilined
                      >
                        <span class="boq-excess-flag">
                          <b-icon pack="fas" icon="triangle-exclamation" size="is-small" />
                        </span>
                      </b-tooltip>
                      <span v-else class="boq-status boq-status--ok">{{ $t('boq_status_ok') }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </template>
          </div>
        </div>

        <!-- Línea marcada "sin mediciones": en vez del árbol WBS, un mensaje. -->
        <div
          v-else
          class="bg-white rounded-lg border border-gray-200 flex-1 min-h-0 flex flex-col items-center justify-center gap-2 text-gray-400"
          style="padding: 2rem"
        >
          <b-icon pack="fas" icon="link-slash" size="is-medium"></b-icon>
          <p class="text-center" style="font-size: 12px">{{ $t('boq_no_measurement_message') }}</p>
        </div>
      </template>

      <!-- Sin selección: ocupa todo el alto/ancho del contenedor, centrado. -->
      <div v-else class="bg-white rounded-lg border border-gray-200 flex flex-col items-center justify-center gap-3 text-gray-400 h-full w-full" style="padding: 2rem">
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-list-checks" aria-hidden="true"><path d="M13 5h8"></path><path d="M13 12h8"></path><path d="M13 19h8"></path><path d="m3 17 2 2 4-4"></path><path d="m3 7 2 2 4-4"></path></svg>
        <p class="text-center" style="font-size: 12px">Selecciona una medición para iniciar la vinculación</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import EpcPanel from '../epc-panel.vue'
import ExpandableList from './components/ExpandableList/ExpandableList.vue'
import WbsTree from './components/WbsTree/WbsTree.vue'

export default {
  name: 'BoqLinkManager',

  components: { EpcPanel, ExpandableList, WbsTree },

  props: {
    projectId: { type: [String, Number], default: null },
    // Array de BOQs con sus items/detalles: [{ id, name, ..., items: [details] }]
    boqs: { type: Array, default: () => [] },
  },

  data() {
    return {
      selectedItem: null,   // detalle (línea) crudo seleccionado, para el panel derecho
      selectedNodeId: null, // id del nodo seleccionado, para resaltar en el árbol
      // Árbol WBS: grupos raíz (los niveles inferiores se cargan de forma lazy).
      wbsNodes: [],
      wbsLoading: false,
      wbsLoaded: false,
      wbsError: false,
      // Selección por línea BOQ: { [itemId]: { checked: {ecId:true}, quantities: {ecId:valor} } }.
      // `checked` = mediciones vinculadas (assignments.boq_detail_id). Se carga/guarda vía API.
      linkState: {},
      isSavingLinks: false,
      // Modo del panel derecho: 'linking' (árbol) | 'linked' (lista de vinculadas).
      viewMode: 'linking',
      // Estado pendiente (se persiste con Guardar): la línea NO se vincula con mediciones.
      noMeasurementLink: false,
      // Info de display de cada medición (nombre/unidad/cantidad/código) por id, para el modo lista.
      medInfoById: {},
    }
  },

  computed: {
    // Mapea boqs → nodos del ExpandableList (BOQ = grupo, items = hijos).
    nodes() {
      return (this.boqs || []).map((b) => {
        const items = b.items || []
        return {
          id: 'boq-' + b.id,
          code: b.code || '',
          title: b.name,
          meta: items.length + ' ' + this.$t('lines').toLowerCase(),
          amount: this.formatMoney(
            b.details_sum_price_total != null
              ? b.details_sum_price_total
              : items.reduce((acc, it) => acc + (Number(it.price_total) || 0), 0)
          ),
          children: items.map((it) => ({
            id: 'item-' + it.id,
            code: it.code,
            subtitle: it.line_pedido || '', // item / línea de pedido
            title: it.description || '—',
            meta: (it.quantity != null ? this.formatQty(it.quantity) : '') + ' ' + (it.unit || ''),
            amount: this.formatMoney(it.price_total),
            badge: Number(it.assignments_count || 0), // nº de mediciones ya vinculadas
            noLink: !!it.no_measurement_link, // línea marcada "sin mediciones"
            raw: it, // detalle crudo para el panel derecho
          })),
        }
      })
    },
    // Cantidad total del item seleccionado, formateada.
    qtyText() {
      if (!this.selectedItem || this.selectedItem.quantity == null) return '—'
      return this.formatQty(this.selectedItem.quantity)
    },
    // Selección (checked/cantidades) de la línea BOQ actualmente seleccionada.
    currentChecked() {
      if (!this.selectedItem) return {}
      const entry = this.linkState[this.selectedItem.id]
      return entry ? entry.checked : {}
    },
    currentQuantities() {
      if (!this.selectedItem) return {}
      const entry = this.linkState[this.selectedItem.id]
      return entry ? entry.quantities : {}
    },
    // Nº de mediciones/EC marcadas para la línea actual.
    selectedCount() {
      return Object.keys(this.currentChecked).length
    },
    // Σ de cantidades asignadas (mediciones marcadas) de la línea actual.
    assignedNumber() {
      const q = this.currentQuantities
      return Object.keys(this.currentChecked).reduce((acc, key) => {
        const n = Number(String(q[key]).replace(',', '.'))
        return acc + (Number.isFinite(n) ? n : 0)
      }, 0)
    },
    assignedText() {
      if (this.noMeasurementLink) return '—'
      return this.formatQty(this.assignedNumber)
    },
    // Cobertura completa: pendiente <= 0. Deshabilita marcar MÁS mediciones en el árbol
    // (permite una sola que cruce el total: se marca estando aún con pendiente > 0).
    coverageFull() {
      if (!this.selectedItem) return false
      const total = Number(this.selectedItem.quantity) || 0
      if (total <= 0) return false
      return (total - this.assignedNumber) <= 1e-9
    },
    // Pendiente = total de la línea − asignado. Si es negativo (excedente) se muestra 0.
    pendingText() {
      if (this.noMeasurementLink) return '—'
      if (!this.selectedItem || this.selectedItem.quantity == null) return '—'
      const pending = Number(this.selectedItem.quantity) - this.assignedNumber
      if (pending < 0) return '0'
      return this.formatQty(pending)
    },
    // El toggle "sin mediciones" solo aplica a líneas sin mediciones vinculadas ni selección
    // pendiente (si ya tiene mediciones, no debe aparecer la opción).
    canMarkNoLink() {
      if (!this.selectedItem) return false
      return Number(this.selectedItem.assignments_count || 0) === 0 && this.selectedCount === 0
    },
    // Estado de cobertura de la línea actual (según lo seleccionado): ok / excedente / déficit.
    coverageStatusKey() {
      if (this.noMeasurementLink) return 'boq_status_no_measurement'
      if (!this.selectedItem || !this.selectedCount) return null
      const total = Number(this.selectedItem.quantity) || 0
      if (total <= 0) return null
      const pending = total - this.assignedNumber
      if (Math.abs(pending) < 1e-9) return 'boq_status_ok'
      if (pending < 0) return 'boq_status_excess_other'   // texto: "…certificarse en otra BOQ"
      return 'boq_status_deficit'
    },
    coverageStatusStyle() {
      const k = this.coverageStatusKey
      if (k === 'boq_status_ok') return { color: '#1f7a44', background: '#e3fcec' }
      if (k === 'boq_status_excess_other') return { color: '#b42318', background: '#fee4e2' }
      if (k === 'boq_status_deficit') return { color: '#9a6a00', background: '#fef3c7' }
      if (k === 'boq_status_no_measurement') return { color: '#57534e', background: '#f5f5f4' }
      return {}
    },
    // Lista de mediciones vinculadas (marcadas) para el modo lista, con marca de excedente
    // según la regla de acumulado (orden por id).
    linkedMedsMarked() {
      if (!this.selectedItem) return []
      const entry = this.linkState[this.selectedItem.id]
      const seen = entry && entry.seen ? entry.seen : {}
      const checked = entry && entry.checked ? entry.checked : {}
      const q = this.currentQuantities
      const total = Number(this.selectedItem.quantity) || 0
      const meds = Object.keys(seen).map((k) => {
        const id = Number(k)
        const info = this.medInfoById[id] || { id }
        const qtyRaw = q[id] != null ? q[id] : (info.quantity != null ? info.quantity : 0)
        const qty = Number(String(qtyRaw).toString().replace(',', '.')) || 0
        return { ...info, id, quantity: qty, linked: !!checked[id] }
      }).sort((a, b) => a.id - b.id)
      // El excedente se calcula solo sobre las que siguen vinculadas (las que se guardarán).
      let running = 0
      return meds.map((m) => {
        let excess = false
        let coveredQty = m.quantity
        let excessQty = 0
        if (m.linked && total > 0 && m.quantity > 0) {
          if (running >= total - 1e-9) {
            // Empieza fuera del total: todo excede (caso que no se debería poder guardar).
            excess = true
            coveredQty = 0
            excessQty = m.quantity
          } else if (running + m.quantity > total + 1e-9) {
            // Cruza el límite: parte cubre, parte excede.
            excess = true
            coveredQty = total - running
            excessQty = m.quantity - coveredQty
          }
          running += m.quantity
        }
        return { ...m, _excess: excess, _coveredQty: coveredQty, _excessQty: excessQty }
      })
    },
  },

  methods: {
    onSelect(node) {
      // Solo los hijos (items) tienen `raw`; los grupos (BOQ) solo expanden.
      if (node && node.raw) {
        this.selectedItem = node.raw
        this.selectedNodeId = node.id
        this.noMeasurementLink = !!node.raw.no_measurement_link
        this.ensureLinkEntry(node.raw.id)
        this.ensureWbsRoots()
        this.loadLinks(node.raw.id)
      }
    },
    clearSelection() {
      this.selectedItem = null
      this.selectedNodeId = null
    },

    // ---- Carga del árbol WBS (lazy) --------------------------------------

    // Carga los grupos raíz una sola vez (al primer item seleccionado).
    async ensureWbsRoots() {
      if (this.wbsLoaded || this.wbsLoading || !this.projectId) return
      this.wbsLoading = true
      this.wbsError = false
      try {
        const groups = await this.fetchGroups(null)
        this.wbsNodes = groups.map((g) => this.mapGroupNode(g))
        this.wbsLoaded = true
      } catch (e) {
        this.wbsError = true
      } finally {
        this.wbsLoading = false
      }
    },

    // Loader que pasa al WbsTree: devuelve los hijos del nodo según su tipo.
    async loadWbsChildren(node) {
      if (node.type === 'group') {
        if (node.groupChildrenCount > 0) {
          const groups = await this.fetchGroups(node.groupId)
          return groups.map((g) => this.mapGroupNode(g))
        }
        const activities = await this.fetchActivities(node.groupId)
        return activities.map((a) => this.mapActivityNode(a))
      }
      if (node.type === 'activity' || node.type === 'subactivity') {
        // Con subactividades → subactividades; si no → mediciones (EC).
        if (node.subCount > 0) {
          const subs = await this.fetchSubactivities(node.activityId)
          return subs.map((s) => this.mapSubactivityNode(s))
        }
        const mediciones = await this.fetchAssignments(node.activityId)
        return mediciones.map((m) => this.mapEcNode(m))
      }
      return []
    },

    // ---- Llamadas API ----------------------------------------------------

    async fetchGroups(parentId) {
      const params = {}
      if (parentId) params.parent_id = parentId
      const { data } = await axios.get(`/api/v2/project/${this.projectId}/activity/groups`, { params })
      return (data && data.groups) ? data.groups : []
    },
    async fetchActivities(groupId) {
      const params = {
        p: this.projectId,
        group_id: groupId,
        order_field: 'planning_code',
        order_dir: 'asc',
        s_num: 1,
      }
      const { data } = await axios.get('/api/v2/activities', { params })
      return (data && data.activities) ? data.activities : []
    },
    async fetchSubactivities(activityId) {
      const { data } = await axios.get(`/api/v2/activity/${activityId}/subactivities`, { params: { s_num: 1 } })
      return (data && data.success && Array.isArray(data.subactivities)) ? data.subactivities : []
    },
    async fetchAssignments(activityId) {
      const { data } = await axios.get(`/api/v2/activity/${activityId}/assignments`, { params: { with_boq_root: 1 } })
      return (data && data.success && Array.isArray(data.data)) ? data.data : []
    },

    // ---- Mapeo API → nodos del WbsTree -----------------------------------

    mapGroupNode(g) {
      const count = g.children_count || 0
      return {
        id: 'g-' + g.id,
        type: 'group',
        groupId: g.id,
        groupChildrenCount: count,
        code: g.code || '',
        title: g.name || '—',
        color: g.color || null,
        count: count > 0 ? count : null,
        _expandable: true,
        _open: false,
        _loaded: false,
        _loading: false,
        children: [],
      }
    },
    mapActivityNode(a) {
      const sub = a.subactivities_num || 0
      return {
        id: 'a-' + a.id,
        type: 'activity',
        activityId: a.id,
        subCount: sub,
        code: a.planning_code || '',
        title: a.name || '—',
        count: sub > 0 ? sub + ' ' + this.$t('subactivities').toLowerCase() : null,
        _expandable: true,
        _open: false,
        _loaded: false,
        _loading: false,
        children: [],
      }
    },
    mapSubactivityNode(s) {
      const sub = s.subactivities_num || 0
      return {
        id: 's-' + s.id,
        type: 'subactivity',
        activityId: s.id,
        subCount: sub,
        title: s.name || '—',
        meta: s.planning_code || '',
        _expandable: true,
        _open: false,
        _loaded: false,
        _loading: false,
        children: [],
      }
    },
    mapEcNode(m) {
      return {
        id: 'e-' + m.id,
        type: 'ec',
        ecId: m.id,
        title: m.description || m.label || m.code || '—',
        code: m.code || null,
        unit: m.unit_name || '',
        total: m.total != null ? m.total : null,
        // Línea BOQ a la que ya está vinculada esta medición (o null).
        linkedBoqId: m.boq_detail_id != null ? m.boq_detail_id : null,
        _expandable: false,
      }
    },

    // ---- Selección (checked + cantidades) por línea BOQ ------------------

    ensureLinkEntry(itemId) {
      if (!this.linkState[itemId]) {
        // `seen` = mediciones mostradas en el modo lista (vinculadas + tocadas en la sesión);
        // al desmarcar NO se quitan de `seen` (siguen visibles hasta Guardar).
        this.$set(this.linkState, itemId, { checked: {}, quantities: {}, seen: {} })
      }
    },

    // Carga las mediciones ya vinculadas a la línea BOQ (pre-marca los checks del árbol).
    async loadLinks(boqDetailId) {
      if (!this.projectId || !boqDetailId) return
      this.ensureLinkEntry(boqDetailId)
      try {
        const { data } = await axios.get(
          `/api/v2/project/${this.projectId}/boq-details/${boqDetailId}/assignments`
        )
        if (data && data.success) {
          // Marca las mediciones vinculadas y precarga su cantidad (para Asignado/Pendiente).
          const checked = {}
          const quantities = {}
          const list = (data.assignments && data.assignments.length)
            ? data.assignments
            : (data.assignment_ids || []).map((id) => ({ id, quantity: null }))
          const seen = {}
          list.forEach((m) => {
            checked[m.id] = true
            seen[m.id] = true
            if (m.quantity != null) quantities[m.id] = m.quantity
            // Info de display para el modo lista (nombre/unidad/cantidad/código).
            this.$set(this.medInfoById, m.id, {
              id: m.id,
              name: m.name != null ? m.name : (this.medInfoById[m.id] && this.medInfoById[m.id].name) || null,
              code: m.code != null ? m.code : (this.medInfoById[m.id] && this.medInfoById[m.id].code) || null,
              unit: m.unit != null ? m.unit : (this.medInfoById[m.id] && this.medInfoById[m.id].unit) || null,
              quantity: m.quantity != null ? m.quantity : null,
            })
          })
          this.$set(this.linkState, boqDetailId, { checked, quantities, seen })
        }
      } catch (e) {
        // silencioso: si falla, el usuario parte sin vínculos previos
      }
    },

    // Guarda las mediciones vinculadas (solo el tag boq_detail_id). El backend valida
    // que la suma de cantidades no supere la de la línea BOQ.
    async saveLinks() {
      if (!this.selectedItem) return
      const itemId = this.selectedItem.id

      // Línea marcada "sin mediciones": persiste solo el flag (el backend limpia vínculos).
      if (this.noMeasurementLink) {
        this.isSavingLinks = true
        try {
          const { data } = await axios.put(
            `/api/v2/project/${this.projectId}/boq-details/${itemId}/assignments`,
            { links: [], no_measurement_link: true }
          )
          if (data && data.success) {
            this.$notify.success('measurements_linked_ok')
            this.$set(this.selectedItem, 'no_measurement_link', true)
            this.$set(this.selectedItem, 'assignments_count', 0)
            this.$set(this.linkState, itemId, { checked: {}, quantities: {}, seen: {} })
          } else {
            this.$notify.error((data && data.message) || 'error')
          }
        } catch (e) {
          const msg = e && e.response && e.response.data && e.response.data.message
          this.$notify.error(msg || 'error')
        } finally {
          this.isSavingLinks = false
        }
        return
      }

      const entry = this.linkState[itemId] || { checked: {}, quantities: {} }
      const ids = Object.keys(entry.checked).map((k) => Number(k))
      // Cada vínculo con su cantidad (la propia de la medición, tal como se ve en el árbol).
      const links = ids.map((id) => ({
        assignment_id: id,
        quantity: Number(String(entry.quantities[id] != null ? entry.quantities[id] : 0).replace(',', '.')) || 0,
      }))

      // Validación en cliente (invariante al orden): se permite que UNA sola medición exceda
      // el total (excedente para dividir con Split). Si, quitando la de mayor cantidad, el resto
      // ya iguala/supera el total → hay ≥2 excediendo → no se guarda.
      const boqQty = Number(this.selectedItem.quantity) || 0
      if (boqQty > 0) {
        const qtys = links.map((l) => Number(l.quantity) || 0).filter((q) => q > 0)
        const sum = qtys.reduce((a, b) => a + b, 0)
        const max = qtys.length ? Math.max(...qtys) : 0
        if (sum - max >= boqQty - 1e-9) {
          this.$notify.error('boq_measurement_fully_exceeds')
          return
        }
      }

      this.isSavingLinks = true
      try {
        const { data } = await axios.put(
          `/api/v2/project/${this.projectId}/boq-details/${itemId}/assignments`,
          { links, no_measurement_link: false }
        )
        if (data && data.success) {
          this.$notify.success('measurements_linked_ok')
          this.$set(this.selectedItem, 'no_measurement_link', false)
          // Tras guardar, `seen` = solo las que quedaron vinculadas (las quitadas desaparecen).
          const e = this.linkState[itemId]
          if (e) this.$set(e, 'seen', { ...e.checked })
        } else {
          this.$notify.error((data && data.message) || 'error')
        }
      } catch (e) {
        const msg = e && e.response && e.response.data && e.response.data.message
        this.$notify.error(msg || 'error')
      } finally {
        this.isSavingLinks = false
      }
    },
    onCheck({ node, checked }) {
      if (!this.selectedItem) return
      const itemId = this.selectedItem.id
      this.ensureLinkEntry(itemId)
      const entry = this.linkState[itemId]
      const key = node.ecId != null ? node.ecId : node.id
      if (checked) {
        this.$set(entry.checked, key, true)
        if (!entry.seen) this.$set(entry, 'seen', {})
        this.$set(entry.seen, key, true) // visible en el modo lista aunque luego se desmarque
        // Al marcar, precarga la cantidad con el total de la medición.
        if (entry.quantities[key] == null) {
          this.$set(entry.quantities, key, node.total != null ? node.total : 0)
        }
        // Guarda la info de display de la medición para el modo lista.
        this.$set(this.medInfoById, key, {
          id: key,
          name: node.title != null ? node.title : (this.medInfoById[key] && this.medInfoById[key].name) || null,
          code: node.code != null ? node.code : (this.medInfoById[key] && this.medInfoById[key].code) || null,
          unit: node.unit != null ? node.unit : (this.medInfoById[key] && this.medInfoById[key].unit) || null,
          quantity: node.total != null ? node.total : null,
        })
      } else {
        this.$delete(entry.checked, key)
        this.$delete(entry.quantities, key)
      }
    },
    onQty({ node, value }) {
      if (!this.selectedItem) return
      const itemId = this.selectedItem.id
      this.ensureLinkEntry(itemId)
      const key = node.ecId != null ? node.ecId : node.id
      this.$set(this.linkState[itemId].quantities, key, value)
    },

    // Alterna entre modo Vinculación (árbol) y Mediciones vinculadas (lista).
    toggleViewMode() {
      this.viewMode = this.viewMode === 'linking' ? 'linked' : 'linking'
    },
    // Alterna el flag "sin mediciones" (pendiente; se persiste al Guardar).
    toggleNoMeasurementLink() {
      this.noMeasurementLink = !this.noMeasurementLink
    },
    // Alterna vinculación de una medición desde el modo lista (sin quitarla de la vista
    // hasta Guardar). Al re-marcar restaura su cantidad propia.
    toggleLinked(m) {
      const node = { ecId: m.id, title: m.name, code: m.code, unit: m.unit, total: m.quantity }
      this.onCheck({ node, checked: !(this.currentChecked && this.currentChecked[m.id]) })
    },


    // ---- Utilidades ------------------------------------------------------

    formatQty(v) {
      if (v == null || Number.isNaN(Number(v))) return '—'
      return Number(v).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatMoney(v) {
      if (v == null || Number.isNaN(Number(v))) return '—'
      return Number(v).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
    },
  },
}
</script>

<style scoped>
/* Modo "Mediciones vinculadas": lista de seleccionadas con desvincular + estado. */
.lm-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}
.lm-table th,
.lm-table td {
  padding: 4px 10px;
  border-top: 1px solid #f1f3f7;
  text-align: left;
  color: rgb(30, 48, 70);
}
.lm-table thead th {
  position: sticky;
  top: 0;
  background: #f5f7fb;
  color: #55627d;
  font-weight: 600;
  border-top: 0;
  z-index: 1;
}
.lm-table .is-right { text-align: right; }
.lm-table .is-center { text-align: center; }
.lm-table input[type="checkbox"] { cursor: pointer; accent-color: rgb(58,107,200); }
.lm-row--excess td { background: #fff7ed; }
.lm-row--off td { opacity: .55; }
.lm-ec-icon { color: rgb(58,107,200); flex-shrink: 0; }

/* .boq-status y .boq-excess-flag ahora son globales (front/components/epc-status.scss). */
</style>
